import axios, { AxiosError, AxiosResponse, ResponseType } from "axios"
import { useSocietyStore } from '@/store/society'
import router from '../router'
import { getBearerToken } from '@/utils/authToken'
import { appLogout } from '@/utils/logout'
import { useSnackbar } from '@/composables/useSnackbar'
import i18n from '@/i18n'

/**
 * Default user-facing error for a failed mutation when the caller passes no errorCallback.
 * Shows the backend `detail` if present, a generic message otherwise. This is the system-wide
 * safety net so a failed save/delete is never silent. Callers that own their own feedback
 * (field-level 400s, 409 control flow, polling) opt out by passing an errorCallback — the
 * default only fires on the otherwise-silent path.
 */
const notifyRequestError = (response?: AxiosResponse): void => {
  const detail = (response?.data as { detail?: unknown } | undefined)?.detail
  const text = typeof detail === "string" && detail.trim()
    ? detail
    : (i18n.global.t("common.errors.general") as string)
  useSnackbar().show({ text, color: "error" })
}

export const Api = {
  /**
   * @deprecated Callback style. Target: repository in src/infrastructure/ + use case + store action.
   * BUT: until HttpClient lands in Phase 2.1, this warning is NOT yet actionable — keep using Api.get
   * and do NOT invent a parallel HttpClient. See docs/MIGRATION.md §1.5 for why this warning ships early.
   */
  get: async (url: string, setLoading: Function, callback: Function, errorCallback?: Function, responseTypeOverride?: ResponseType, requestConfig?: { timeout?: number; signal?: AbortSignal }) => {
    setLoading(true)

    try {
      const accessToken = await getBearerToken()
      const locale = localStorage.getItem("locale") || "es"

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/${url}`,
        {
          headers: { Authorization: `Bearer ${accessToken}`, "Accept-Language": locale },
          ...(responseTypeOverride ? { responseType: responseTypeOverride } : {}),
          ...(requestConfig?.timeout ? { timeout: requestConfig.timeout } : {}),
          ...(requestConfig?.signal ? { signal: requestConfig.signal } : {}),
        }
      )
      callback(response)

      if (response.data.environment) {
        // Resolve the store lazily: api.ts can be imported during app bootstrap,
        // before app.use(pinia) runs, so it must not touch Pinia at module scope.
        const societyStore = useSocietyStore()
        societyStore.setId(response.data.environment.society_id)
        societyStore.setPlan(response.data.environment.plan)
      }
    } catch (e) {
      // A request aborted via AbortSignal (superseded by a newer one) is intentional, not a failure.
      // The finally below still runs setLoading(false), so callers passing `signal` must gate their
      // setLoading on a latest-request check (see InvoiceFirewallHistory) to not clear it mid-flight.
      if (axios.isCancel(e)) return

      const error = e as AxiosError
      const response = error.response as AxiosResponse

      if (response && response.data.detail === "Upgrade plan to access this resourse") {
        router.push({ path: "/upgrade_plan" })
      } else if (response && response.data.detail === "Password expired") {
        router.push({ path: "/auth/password_expired" })
      } else if (
        (e === "No current user" || (response && response.status === 401)) ||
        ((e as any).error === "missing_refresh_token" && (e as any).audience === import.meta.env.VITE_AUTH0_AUDIENCE)
      ) {
        appLogout()
      } else if (errorCallback) {
        errorCallback(response)
      }
    } finally {
      setLoading(false)
    }
  },
  /** @deprecated Callback style. Not yet actionable — HttpClient lands in Phase 2.1. See docs/MIGRATION.md §1.5. */
  post: async (url: string, body: object | FormData, setLoading: Function, callback: Function, errorCallback?: Function, responseType?: ResponseType, extraHeaders?: Record<string, string>) => {
    setLoading(true)

    try {
      const accessToken = await getBearerToken()
      const locale = localStorage.getItem("locale") || "es"

      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": locale
      }

      if (!(body instanceof FormData)) {
        headers["Content-Type"] = "application/json"
      }

      if (extraHeaders) {
        Object.assign(headers, extraHeaders)
      }

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/${url}`,
        body,
        { headers, ...(responseType ? { responseType: responseType } : {}) }
      )
      callback(response)
    } catch (e) {
      const error = e as AxiosError
      const response = error.response as AxiosResponse

      if (response && response.status === 401) {
        appLogout()
      } else if (errorCallback) {
        errorCallback(response)
      } else {
        notifyRequestError(response)
      }
    } finally {
      setLoading(false)
    }
  },
  /** @deprecated Callback style. Not yet actionable — HttpClient lands in Phase 2.1. See docs/MIGRATION.md §1.5. */
  put: async (url: string, body: object | FormData, setLoading: Function, callback: Function, errorCallback?: Function) => {
    setLoading(true)

    try {
      const accessToken = await getBearerToken()
      const locale = localStorage.getItem("locale") || "es"

      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": locale
      }

      if (!(body instanceof FormData)) {
        headers["Content-Type"] = "application/json"
      }

      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/api/${url}`,
        body,
        { headers }
      )
      callback(response)
    } catch (e) {
      const error = e as AxiosError
      const response = error.response as AxiosResponse

      if (response && response.status === 401) {
        appLogout()
      } else if (errorCallback) {
        errorCallback(response)
      } else {
        notifyRequestError(response)
      }
    } finally {
      setLoading(false)
    }
  },
  /** @deprecated Callback style. Not yet actionable — HttpClient lands in Phase 2.1. See docs/MIGRATION.md §1.5. */
  patch: async (url: string, body: object | FormData, setLoading: Function, callback: Function, errorCallback?: Function) => {
    setLoading(true)

    try {
      const accessToken = await getBearerToken()
      const locale = localStorage.getItem("locale") || "es"

      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": locale
      }

      if (!(body instanceof FormData)) {
        headers["Content-Type"] = "application/json"
      }

      const response = await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/${url}`,
        body,
        { headers }
      )
      callback(response)
    } catch (e) {
      const error = e as AxiosError
      const response = error.response as AxiosResponse

      if (response && response.status === 401) {
        appLogout()
      } else if (errorCallback) {
        errorCallback(response)
      } else {
        notifyRequestError(response)
      }
    } finally {
      setLoading(false)
    }
  },
  /** @deprecated Callback style. Not yet actionable — HttpClient lands in Phase 2.1. See docs/MIGRATION.md §1.5. */
  delete: async (url: string, setLoading: Function, callback: Function, errorCallback?: Function, body?: object) => {
    setLoading(true)

    try {
      const accessToken = await getBearerToken()
      const locale = localStorage.getItem("locale") || "es"

      const response = await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/${url}`,
        {
          headers: { Authorization: `Bearer ${accessToken}`, "Accept-Language": locale },
          // Optional: some endpoints require a payload on DELETE (e.g. a mandatory reason for
          // revoking). Omitted when absent so existing callers send the exact same request.
          ...(body ? { data: body } : {}),
        }
      )
      callback(response)
    } catch (e) {
      const error = e as AxiosError
      const response = error.response as AxiosResponse

      if (response && response.data.detail === "Upgrade plan to access this resourse") {
        router.push({ path: "/upgrade_plan" })
      }
      else if (e === "No current user" || (response && response.status === 401)) {
        appLogout()
      } else if (errorCallback) {
        errorCallback(response)
      } else {
        notifyRequestError(response)
      }
    } finally {
      setLoading(false)
    }
  },
}
